import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styles from './ExploreDesktop.module.css';
import ExploreItem from './ExploreItem';
import ScenarioItem from './ScenarioItem';
import SearchDropdown from './SearchDropdown';
import GridLoadingIndicator from './GridLoadingIndicator';
import { ScenarioDefinitionListResponse } from '../../api/types/scenario';
import FeaturedScenarioCarousel from './FeaturedScenarioCarousel';
import FeaturedCharacters from './FeaturedCharacters';
import { FaUser, FaBookOpen } from 'react-icons/fa';
import { logoIcon } from '../../assets/icons';
import { MAX_FEATURED_ITEMS } from './Explore'; // Import the constant from parent component

/**
 * Character interface representing the structure of character data
 */
export interface Character {
  id: string;
  username: string;
  display_name: string;
  tagline: string;
  icon: string;
}

/**
 * Props interface for the ExploreDesktop component
 */
interface ExploreDesktopProps {
  characters: Character[];
  scenarios: ScenarioDefinitionListResponse[];
  featuredCharacters?: Character[];
  featuredScenarios?: ScenarioDefinitionListResponse[];
  dropdownCharacters: Character[]; // New prop for dropdown results
  dropdownScenarios: ScenarioDefinitionListResponse[]; // New prop for dropdown results
  searchQuery: string;
  activeSearchQuery?: string;
  activeCategory: string;
  categories: string[];
  contentType: 'characters' | 'scenarios';
  contentTypes: { id: 'characters' | 'scenarios', label: string }[];
  isLoading: boolean;
  error?: string | null;
  sidebarExpanded?: boolean;
  isSearchActive?: boolean;
  handleSearch: (query: string) => void;
  handleSearchSubmit?: (event: React.FormEvent) => void;
  executeSearch?: () => void;
  clearSearch?: () => void;
  handleCategoryChange: (category: string) => void;
  handleContentTypeChange: (type: 'characters' | 'scenarios') => void;
  handleCharacterClick: (character: Character) => void;
  handleScenarioClick: (scenario: ScenarioDefinitionListResponse) => void;
}

// Configuration constants are now defined in the parent Explore.tsx component

/**
 * Desktop view for the Explore feature
 * Optimized for larger screens with a more spacious layout
 */
const ExploreDesktop: React.FC<ExploreDesktopProps> = ({
  characters,
  scenarios,
  featuredCharacters = [],
  featuredScenarios = [],
  dropdownCharacters = [],
  dropdownScenarios = [],
  searchQuery,
  activeSearchQuery = '',
  activeCategory,
  categories,
  contentType,
  contentTypes,
  isLoading,
  error,
  sidebarExpanded = false,
  isSearchActive = false,
  handleSearch,
  handleSearchSubmit,
  executeSearch,
  clearSearch,
  handleCategoryChange,
  handleContentTypeChange,
  handleCharacterClick,
  handleScenarioClick
}) => {
  // Store initial featured content to prevent changes when filtering
  const [displayedFeaturedCharacters, setDisplayedFeaturedCharacters] = useState<Character[]>([]);
  const [displayedFeaturedScenarios, setDisplayedFeaturedScenarios] = useState<ScenarioDefinitionListResponse[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Add a ref to the header container
  const headerRef = useRef<HTMLDivElement>(null);

  // Update header class based on both scroll position and sidebar state
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        // Add or remove scrolled class based on scroll position
        if (window.scrollY > 20) {
          headerRef.current.classList.add(styles.scrolled);
        } else {
          headerRef.current.classList.remove(styles.scrolled);
        }
        
        // Add or remove sidebarExpanded class based on sidebar state
        if (sidebarExpanded) {
          headerRef.current.classList.add(styles.sidebarExpanded);
        } else {
          headerRef.current.classList.remove(styles.sidebarExpanded);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Call once to set initial state
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sidebarExpanded]); // Add sidebarExpanded as a dependency

  // Initialize featured content when data is first available
  useEffect(() => {
    // Only set initial featured content once
    if (!initialDataLoaded) {
      // For characters
      if (featuredCharacters.length) {
        setDisplayedFeaturedCharacters(featuredCharacters);
      } else if (characters.length) {
        setDisplayedFeaturedCharacters(characters.slice(0, MAX_FEATURED_ITEMS));
      }
      
      // For scenarios
      if (featuredScenarios.length) {
        setDisplayedFeaturedScenarios(featuredScenarios);
      } else if (scenarios.length) {
        setDisplayedFeaturedScenarios(scenarios.slice(0, MAX_FEATURED_ITEMS));
      }
      
      // If we have either characters or scenarios, mark as loaded
      if (characters.length || scenarios.length) {
        setInitialDataLoaded(true);
      }
    }
  }, [featuredCharacters, featuredScenarios, characters, scenarios, initialDataLoaded]);
  
  // Update featured content if the featuredCharacters/featuredScenarios props change
  useEffect(() => {
    if (featuredCharacters.length) {
      setDisplayedFeaturedCharacters(featuredCharacters);
    }
    
    if (featuredScenarios.length) {
      setDisplayedFeaturedScenarios(featuredScenarios);
    } else if (scenarios.length && displayedFeaturedScenarios.length === 0) {
      // Fallback: If we still don't have featured scenarios but we have regular scenarios
      setDisplayedFeaturedScenarios(scenarios.slice(0, MAX_FEATURED_ITEMS));
    }
  }, [featuredCharacters, featuredScenarios, scenarios, displayedFeaturedScenarios.length]);

  // Update local search query when prop changes
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Handle local search - just passes to parent
  const handleLocalSearch = useCallback((query: string) => {
    setLocalSearchQuery(query);
    handleSearch(query);
  }, [handleSearch]);

  // Memoized click handlers to prevent re-renders
  const memoizedHandleCharacterClick = useCallback((character: Character) => {
    handleCharacterClick(character);
  }, [handleCharacterClick]);

  const memoizedHandleScenarioClick = useCallback((scenario: ScenarioDefinitionListResponse) => {
    handleScenarioClick(scenario);
  }, [handleScenarioClick]);

  // Memoize the content rendering to prevent unnecessary recalculations
  const renderedContent = useMemo(() => {
    if (error) {
      return <div className={styles.errorMessage}>{error}</div>;
    }
    
    if (isLoading) {
      return <GridLoadingIndicator />;
    }
    
    if (contentType === 'characters') {
      return characters.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No characters found</p>
        </div>
      ) : (
        <div className={styles.characterGrid}>
          {characters.map((character) => (
            <ExploreItem
              key={character.id}
              character={character}
              onClick={memoizedHandleCharacterClick}
            />
          ))}
        </div>
      );
    } else {
      return scenarios.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No scenarios found</p>
        </div>
      ) : (
        <div className={styles.scenarioGrid}>
          {scenarios.map((scenario) => (
            <ScenarioItem
              key={scenario.id}
              scenario={scenario}
              onClick={memoizedHandleScenarioClick}
            />
          ))}
        </div>
      );
    }
  }, [
    error, 
    isLoading,
    contentType, 
    characters, 
    scenarios, 
    memoizedHandleCharacterClick, 
    memoizedHandleScenarioClick
  ]);

  // Memoize the content type buttons to prevent re-rendering when unrelated data changes
  const contentTypeButtons = useMemo(() => (
    <div className={styles.contentTypeSelector}>
      {contentTypes.map((type) => (
        <button 
          key={type.id} 
          className={`${styles.contentTypeButton} ${type.id === contentType ? styles.active : ''}`}
          onClick={() => handleContentTypeChange(type.id)}
          aria-pressed={type.id === contentType}
        >
          <span className={styles.contentTypeIcon}>
            {type.id === 'characters' ? <FaUser /> : <FaBookOpen />}
          </span>
          <span className={styles.contentTypeLabel}>{type.label}</span>
        </button>
      ))}
    </div>
  ), [contentTypes, contentType, handleContentTypeChange]);

  // Memoize the category buttons to prevent re-rendering when unrelated data changes
  const categoryButtons = useMemo(() => (
    <div className={styles.categorySelector}>
      {categories.map((category) => (
        <button 
          key={category} 
          className={`${styles.categoryButton} ${category === activeCategory ? styles.active : ''}`}
          onClick={() => handleCategoryChange(category)}
          aria-pressed={category === activeCategory}
        >
          {category}
        </button>
      ))}
    </div>
  ), [categories, activeCategory, handleCategoryChange]);

  return (
    <div className={styles.exploreDesktopPage}>
      {/* Header section with search bar and toast icon in a row */}
      <div className={`${styles.headerContainer} ${sidebarExpanded ? styles.sidebarExpanded : ''}`} ref={headerRef}>
        {/* Search Bar */}
        <div className={styles.searchContainer}>
          <SearchDropdown
            dropdownCharacters={dropdownCharacters}
            dropdownScenarios={dropdownScenarios}
            searchQuery={localSearchQuery}
            activeSearchQuery={activeSearchQuery}
            isLoading={isLoading}
            handleSearch={handleLocalSearch}
            executeSearch={executeSearch}
            clearSearch={clearSearch}
            handleCharacterClick={memoizedHandleCharacterClick}
            handleScenarioClick={memoizedHandleScenarioClick}
          />
        </div>
        
        {/* Toast Icon */}
        <div className={styles.toastIconContainer}>
          <img 
            src={logoIcon} 
            alt="Hangjam Toast" 
            className={`${styles.toastIcon} ${styles.glowEffect}`} 
            onClick={() => {
              const toastIcon = document.querySelector(`.${styles.toastIcon}`);
              toastIcon?.classList.add(styles.glowAnimation);
              setTimeout(() => {
                toastIcon?.classList.remove(styles.glowAnimation);
              }, 1500); // Animation duration
            }}
          />
        </div>
      </div>
      
      {/* Featured Scenarios Carousel - Use a placeholder div to reserve space */}
      <div className={styles.carouselPlaceholder}>
        {displayedFeaturedScenarios.length > 0 ? (
          <FeaturedScenarioCarousel
            featuredScenarios={displayedFeaturedScenarios}
            onScenarioClick={memoizedHandleScenarioClick}
          />
        ) : (
          <div className={styles.carouselLoading}></div>
        )}
      </div>
      
      {/* Featured Characters */}
      <FeaturedCharacters 
        characters={displayedFeaturedCharacters}
        onCharacterClick={memoizedHandleCharacterClick}
        isLoading={!initialDataLoaded || (isLoading && displayedFeaturedCharacters.length === 0)}
      />
      
      {/* Main Content Section */}
      <div className={styles.mainContent}>
        <div className={styles.contentControls}>
          {/* Content Type Selector */}
          {contentTypeButtons}
          
          {/* Category Tabs - Show for both characters and scenarios */}
          {categoryButtons}
        </div>
        
        {/* Content Grid */}
        <div className={styles.contentGrid}>
          {renderedContent}
        </div>
      </div>
    </div>
  );
};

export default ExploreDesktop; 